/**
 * GA4 に送る URL から、計測に載せてよいクエリだけを残す。
 *
 * LIFF の URL には遷移先を特定するパラメータが付く（連絡帳通知の `patient_id` / `date`、
 * 事業所からの返信の通知の `patient_id` / `thread_id` / `message_id`）。とくに `thread_id` は
 * `{patient_id}_{office_id}_{timestamp}` 形式で利用者 ID と事業所 ID を含むため、そのまま送ると
 * FR-30.3「ID を計測データに含めない」に反する。
 *
 * 許可するキーの一覧は `docs/integrations/ga4.md` の「クエリ文字列の扱い」を正とする。
 */

/**
 * 計測に残すクエリ（`docs/integrations/ga4.md` の表を写したもの）。
 *
 * **キー名の完全一致で判定する。`utm_` の接頭辞一致にはしない** — `utm_patient_id` のような
 * キーを通さないため。**値は検証しない**（値に ID を入れない責務は URL を生成する側）。
 */
const ALLOWED_QUERY_KEYS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
]);

/** 許可したキーだけを残したクエリ文字列を返す（先頭の `?` 込み。1 つも無ければ空文字）。 */
function filterSearch(search: string): string {
  const params = new URLSearchParams(search);
  const kept = new URLSearchParams();
  for (const [key, value] of params) {
    if (ALLOWED_QUERY_KEYS.has(key)) {
      kept.append(key, value);
    }
  }
  const query = kept.toString();
  return query ? `?${query}` : '';
}

/**
 * `page_path` 用。`pathname + 許可したクエリ` を返す。
 */
export function sanitizePagePath(pathname: string, search: string): string {
  return `${pathname}${filterSearch(search)}`;
}

/**
 * `page_location` / `page_referrer` 用。絶対 URL のクエリだけを洗って返す。
 *
 * ハッシュは落とす（LIFF が `#compose` 等を使うだけで ID は載らないが、計測に要らない）。
 * URL として解釈できない値は**そのまま返さず空にする** — 解釈できない以上、中に何が入って
 * いるか保証できないため。`page_referrer` は空文字が渡ることがあり、その場合は空のまま返す。
 */
export function sanitizeAbsoluteUrl(rawUrl: string): string {
  if (!rawUrl) return '';
  try {
    const url = new URL(rawUrl);
    return `${url.origin}${url.pathname}${filterSearch(url.search)}`;
  } catch {
    return '';
  }
}
